import { useMutation } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { TIME_FORMAT } from 'src/constants/date';
import { SIGNAL_BUSINESS_HOURS_FRAGMENT } from 'src/graphql/entities/signal-business-hours/signal-business-hours.fragments';
import { CREATE_SIGNAL_BUSINESS_HOURS_QUERY } from 'src/graphql/entities/signal-business-hours/signal-business-hours.operations';
import {
  SignalBusinessHoursMutationInput,
  SignalBusinessHoursQueryResponse,
} from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetSignalBusinessHoursQuery } from './use-signal-business-hours';

export interface CreateSignalBusinessHoursMutation {
  createSignalBusinessHours: SignalBusinessHoursQueryResponse[];
}

export type CreateSignalBusinessHoursMutationParams = {
  data: SignalBusinessHoursMutationInput;
};

export const useCreateSignalBusinessHours = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [createSignalBusinessHoursMutation, { loading: isCreating }] = useMutation<
    CreateSignalBusinessHoursMutation,
    CreateSignalBusinessHoursMutationParams
  >(CREATE_SIGNAL_BUSINESS_HOURS_QUERY, {
    update(cache, { data }) {
      if (!data?.createSignalBusinessHours?.length) {
        return;
      }
      cache.modify({
        fields: {
          getSignalBusinessHours(cachedRef = {}) {
            const cachedData =
              cachedRef as GetSignalBusinessHoursQuery['getSignalBusinessHours'];

            const records = data.createSignalBusinessHours
              .map((data) =>
                cache.writeFragment({
                  data,
                  fragment: SIGNAL_BUSINESS_HOURS_FRAGMENT,
                }),
              )
              .filter(Boolean);

            return {
              records: [...(cachedData.records?.length ? cachedData.records : []), ...records],
              metadata: {
                ...cachedData.metadata,
                totalRecords: (cachedData.metadata?.totalRecords ?? 0) + 1,
              },
            };
          },
        },
        broadcast: false,
      });
    },
    optimisticResponse: (params) => ({
      createSignalBusinessHours: [
        {
          ...params.data,
          __typename: 'SignalBusinessHours',
          id: new Date().getTime().toString(),
          signalBusinessHoursId: new Date().getTime().toString(),
          createdAt: new Date().toISOString(),
        },
      ],
    }),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create Signal Business Hours, please try again');
    },
  });

  const createSignalBusinessHours = useCallback(
    async (signalBusinessHours: {
      weekDay: number;
      timeFrom: dayjs.Dayjs;
      timeTo: dayjs.Dayjs;
      urgency: string;
    }) => {
      if (partnerId === null) {
        console.error('Cannot create Signal business hours. Current PartnerId is not found');
        return;
      }
      return await createSignalBusinessHoursMutation({
        variables: {
          data: {
            weekDay: signalBusinessHours.weekDay,
            timeFrom: signalBusinessHours.timeFrom.format(TIME_FORMAT),
            timeTo: signalBusinessHours.timeTo.format(TIME_FORMAT),
            urgency: signalBusinessHours.urgency,
            partnerId,
          },
        },
      });
    },
    [createSignalBusinessHoursMutation, partnerId],
  );

  return {
    createSignalBusinessHours,
    isCreating,
  };
};
